// src/utils/userManagement.js
import { db } from '../firebase/firebaseConfig';
import { 
  doc, 
  setDoc, 
  deleteDoc, 
  collection, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';

// Fetch admin emails from environment variables
const adminEmails = process.env.REACT_APP_ADMIN_EMAILS
  ? process.env.REACT_APP_ADMIN_EMAILS.split(',').map(email => email.trim())
  : ['julian.pirvu@gmail.com']; // Default admin email

const ALLOWED_ROLES = ['admin', 'staff', 'employee', 'driver', 'brochure'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Adds or updates a user in the 'approvedUsers' collection.
 * @param {Object} userData - The user data containing email, name, and role.
 * @returns {Object} - Result of the operation with success status and message.
 */
export const addApprovedUser = async (userData) => {
  try {
    const normalizedEmail = userData.email?.trim().toLowerCase();
    const isBrochureUser = userData.role === 'brochure';

    if (!normalizedEmail || !EMAIL_PATTERN.test(normalizedEmail)) {
      throw new Error('A valid email address is required');
    }

    if (!isBrochureUser && !normalizedEmail.endsWith('@gmail.com')) {
      throw new Error('Only Gmail addresses are allowed for this role');
    }

    // Validate required fields
    if (!userData.name || !userData.role) {
      throw new Error('Name and role are required');
    }

    if (!ALLOWED_ROLES.includes(userData.role)) {
      throw new Error('Invalid role supplied');
    }

    console.log('Received user data:', userData); // Debug log

    // Use email as document ID in 'approvedUsers'
    const approvedUserRef = doc(db, 'approvedUsers', normalizedEmail);

    // Check if user is already approved
    const q = query(collection(db, 'approvedUsers'), where('email', '==', normalizedEmail));
    const approvedUserSnapshot = await getDocs(q);
    
    if (!approvedUserSnapshot.empty) {
      throw new Error('User is already approved');
    }

    // Prepare user data for Firestore
    const userDataForFirestore = {
      email: normalizedEmail,
      name: userData.name,
      displayName: userData.name, // Ensure displayName is set
      role: userData.role, // Use the role from the form
      createdAt: new Date()
    };

    console.log('Saving user data to Firestore:', userDataForFirestore); // Debug log

    // Add user to 'approvedUsers'
    await setDoc(approvedUserRef, userDataForFirestore, { merge: true });

    return {
      success: true,
      message: userData.role === 'admin' 
        ? 'Admin user added successfully.' 
        : 'User added successfully.'
    };
  } catch (error) {
    console.error('Error adding approved user:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Removes a user from the 'approvedUsers' collection.
 * @param {string} email - The email of the user to remove.
 * @returns {Object} - Result of the operation.
 */
export const removeApprovedUser = async (email) => {
  try {
    if (!email) {
      throw new Error('Email is required for user removal');
    }

    const approvedUserRef = doc(db, 'approvedUsers', email);
    await deleteDoc(approvedUserRef);

    return { 
      success: true,
      message: 'User removed successfully'
    };
  } catch (error) {
    console.error('Error removing approved user:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Removes a user from the 'users' collection.
 * @param {string} uid - The UID of the user to remove.
 * @returns {Object} - Result of the operation.
 */
export const removeUser = async (uid) => {
  try {
    if (!uid) {
      throw new Error('User ID is required for user removal');
    }

    await deleteDoc(doc(db, 'users', uid));
    
    return { 
      success: true,
      message: 'User removed successfully'
    };
  } catch (error) {
    console.error('Error removing user:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Optional: Function to check if an email is in the admin list
export const isAdminEmail = (email) => {
  return adminEmails.includes(email);
};

// Optional: Function to validate user data
export const validateUserData = (userData) => {
  const errors = [];
  
  if (!userData.email) {
    errors.push('Email is required');
  } else if (!EMAIL_PATTERN.test(userData.email.trim().toLowerCase())) {
    errors.push('A valid email address is required');
  } else if (userData.role !== 'brochure' && !userData.email.trim().toLowerCase().endsWith('@gmail.com')) {
    errors.push('Only Gmail addresses are allowed for this role');
  }
  
  if (!userData.name || userData.name.trim() === '') {
    errors.push('Name is required');
  }
  
  if (!userData.role || !ALLOWED_ROLES.includes(userData.role)) {
    errors.push('Valid role is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
